import * as React from "react";
import { useNavigate } from "react-router";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import FeatureSwitch from "../common/FeatureSwitch";
import { useSelectedSport } from "../common/context/sport";

import { ASK_VOLLEY_ROUTE, ASK_VOLLEY_SPORTS } from "./constants";

export interface AskVolleyLauncherProps {
    /** Matches the size of the icon buttons it sits beside in the header. */
    size?: IconButtonProps["size"];
}

/**
 * The persistent way in.
 *
 * Ask Volley answers support questions, which makes "wherever you are, ask"
 * the behaviour to have. It lives in the top bar beside the support-chat
 * button rather than as a floating corner button: the corner is where every
 * full-width call to action ends and where the workout play bar is fixed, so
 * a button there either covered the former or was covered by the latter.
 */
function Launcher({ size }: AskVolleyLauncherProps): React.JSX.Element {
    const navigate = useNavigate();

    return (
        <Tooltip title="Ask Volley">
            <IconButton
                aria-label="Ask Volley"
                color="inherit"
                size={size}
                onClick={() => navigate(ASK_VOLLEY_ROUTE)}
            >
                <AutoAwesomeIcon />
            </IconButton>
        </Tooltip>
    );
}

/**
 * The launcher, behind the same two gates as the home-screen prompt: the
 * `ASK_VOLLEY_HOME_PROMPT` feature, and a sport whose content is authored.
 *
 * Same gates on purpose. A user who cannot be shown the widget cannot be shown
 * a button that opens the same chat, and a second flag would let those two
 * drift into a state where one is reachable and the other is not.
 */
export default function AskVolleyLauncher({
    size,
}: AskVolleyLauncherProps): React.ReactNode {
    const { selected: sport } = useSelectedSport();

    return (
        <FeatureSwitch
            flag="ASK_VOLLEY_HOME_PROMPT"
            source="user_feature"
            enabled={<Launcher size={size} />}
            default={null}
            additionalCondition={() => ASK_VOLLEY_SPORTS.has(sport)}
        />
    );
}
